<template>
  <div class="lotmap">

    <!-- Toolbar: venue picker + add action -->
    <div class="lotmap-toolbar">
      <div class="toolbar-venue">
        <label for="venueSelect" class="toolbar-label">Venue</label>
        <b-form-select
          id="venueSelect"
          v-model="selectedVenue"
          :options="venueOptions"
          @change="onVenueChange"
          class="venue-select"
        >
          <template #first>
            <b-form-select-option :value="null" disabled>— Select a venue —</b-form-select-option>
          </template>
        </b-form-select>
      </div>

      <div class="toolbar-actions">
        <b-badge v-if="selectedVenue" variant="light" class="count-badge">
          {{ lots.length }} {{ lots.length === 1 ? 'mapping' : 'mappings' }}
        </b-badge>
        <b-button
          variant="primary"
          :disabled="!selectedVenue"
          @click="openAddModal"
          v-b-tooltip.hover
          :title="selectedVenue ? 'Add a new lot mapping' : 'Select a venue first'"
        >
          <b-icon icon="plus-circle" class="mr-1"></b-icon> Add Mapping
        </b-button>
      </div>
    </div>

    <!-- Modal for adding a new record -->
    <b-modal 
      v-model="showModal" 
      :title="isEditing ? 'Edit Lot Mapping' : 'Add New Lot Mapping'" 
      size="lg" 
      centered
      @ok="handleModalOk"
      body-class="p-0"
      hide-header-close
    >
      <div class="p-4">
        <!-- Core Details -->
        <h6 class="text-muted text-uppercase mb-3 small font-weight-bold">Core Information</h6>
        <div class="row">
          <div class="col-md-6">
            <b-form-group label="Venue Name" label-class="font-weight-500">
              <b-form-input
                v-model="selectedVenue"
                readonly
                class="bg-light"
              ></b-form-input>
            </b-form-group>
          </div>
          <div class="col-md-6">
            <b-form-group label="SpotJockey Section" label-class="font-weight-bold text-primary">
              <b-form-input
                v-model="newRecord.section"
                placeholder="e.g. Lot A"
                required
                size="lg"
                autofocus
              ></b-form-input>
            </b-form-group>
          </div>
        </div>

        <hr class="my-4">

        <!-- Secondary Marketplaces -->
        <h6 class="text-muted text-uppercase mb-3 small font-weight-bold">Marketplace Mappings</h6>
        <div class="row">
          <div class="col-md-6 col-lg-3">
            <b-form-group label="StubHub" label-class="font-weight-500">
              <b-form-input
                v-model="newRecord.stubhub_location_name"
                list="stubHubList"
                placeholder="Search or type..."
                autocomplete="off"
                class="table-cell-input"
              ></b-form-input>
            </b-form-group>
          </div>
          <div class="col-md-6 col-lg-3">
             <b-form-group label="Vivid Seats" label-class="font-weight-500">
              <b-form-input
                v-model="newRecord.vividseats_location_name"
                list="vividSeatsList"
                placeholder="Search or type..."
                autocomplete="off"
                class="table-cell-input"
              ></b-form-input>
            </b-form-group>
          </div>
          <div class="col-md-6 col-lg-3">
            <b-form-group label="SeatGeek" label-class="font-weight-500">
              <b-form-input
                v-model="newRecord.seatgeek_location_name"
                list="seatGeekList"
                placeholder="Search or type..."
                autocomplete="off"
                class="table-cell-input"
              ></b-form-input>
            </b-form-group>
          </div>
          <div class="col-md-6 col-lg-3">
            <b-form-group label="GoTickets" label-class="font-weight-500">
              <b-form-input
                v-model="newRecord.gotickets_location_name"
                placeholder="Type section name..."
                autocomplete="off"
                class="table-cell-input"
              ></b-form-input>
            </b-form-group>
          </div>
        </div>

        <hr class="my-4">

        <!-- Primary Providers -->
        <h6 class="text-muted text-uppercase mb-3 small font-weight-bold">Parking Providers</h6>
        <div class="bg-light p-3 rounded border">
          <div class="row">
            <div class="col-md-6">
              <b-form-group label="JustPark">
                <b-form-select
                  v-model="newRecord.justpark_location_name"
                  :options="justParkOptions"
                  class="table-cell-select"
                ></b-form-select>
              </b-form-group>
            </div>
            <div class="col-md-6">
              <b-form-group label="ParkWhiz">
                <b-form-select
                  v-model="newRecord.parkwhiz_location_name"
                  :options="pwOptions"
                  class="table-cell-select"
                ></b-form-select>
              </b-form-group>
            </div>
            <div class="col-md-6">
              <b-form-group label="ParkMobile" class="mb-md-0">
                <b-form-select
                  v-model="newRecord.parkmobile_location_name"
                  :options="pmOptions"
                  class="table-cell-select"
                ></b-form-select>
              </b-form-group>
            </div>
            <div class="col-md-6">
              <b-form-group label="SpotHero" class="mb-0">
                <b-form-select
                  v-model="newRecord.spothero_location_name"
                  :options="spthroOptions"
                  class="table-cell-select"
                ></b-form-select>
              </b-form-group>
            </div>
          </div>
        </div>
      </div>

      <template #modal-footer="{ ok, cancel }">
        <b-button variant="outline-secondary" @click="cancel" class="px-4 border-0">Cancel</b-button>
        <b-button variant="primary" size="lg" @click="ok" class="px-5 shadow-sm">Save Mapping</b-button>
      </template>
    </b-modal>

    <!-- Read-only mappings table (click a row to edit) -->
    <div class="table-responsive">
      <vue-good-table
        :columns="columns"
        :rows="lots"
        :isLoading="tableBusy"
        :lineNumbers="false"
        :search-options="{ enabled: true, placeholder: 'Search sections or marketplace names...' }"
        :pagination-options="{
          enabled: true,
          perPage: 15,
          perPageDropdown: [15, 30, 50],
          dropdownAllowAll: true,
          nextLabel: 'Next',
          prevLabel: 'Prev',
          rowsPerPageLabel: 'Rows per page',
        }"
        styleClass="vgt-table striped condensed clickable-rows"
        @on-row-click="onRowClick"
      >
        <template #table-actions>
          <span class="vgt-left vgt-ml-2">
            <b-spinner small type="grow" v-if="tableBusy" class="mr-2"></b-spinner>
          </span>
        </template>

        <template #table-row="props">
          <!-- Section: the key identifier for the row -->
          <span v-if="props.column.field === 'section'" class="section-cell">
            {{ props.row.section || '—' }}
          </span>

          <!-- Row actions -->
          <span v-else-if="props.column.field === 'actions'">
            <div class="action-icons">
              <b-icon
                icon="pencil-square"
                variant="primary"
                class="action-icon"
                v-b-tooltip.hover
                title="Edit mapping"
                @click="editRow(props.row)"
              ></b-icon>
              <b-icon
                icon="files"
                variant="info"
                class="action-icon"
                v-b-tooltip.hover
                title="Duplicate mapping"
                @click="duplicateRow(props.row)"
              ></b-icon>
              <b-icon
                icon="trash"
                variant="danger"
                class="action-icon"
                v-b-tooltip.hover
                title="Delete mapping"
                @click="deleteRow(props.row.id)"
              ></b-icon>
            </div>
          </span>

          <!-- Every mapping value: show it, or a muted dash when unset -->
          <span v-else>
            <span v-if="props.row[props.column.field]" class="mapping-value">
              {{ props.row[props.column.field] }}
            </span>
            <span v-else class="mapping-empty">—</span>
          </span>
        </template>

        <template #emptystate>
          <div class="empty-state">
            <b-icon :icon="selectedVenue ? 'inboxes' : 'geo-alt'" class="empty-icon"></b-icon>
            <p class="mb-2">
              {{ selectedVenue
                ? 'No lot mappings for this venue yet.'
                : 'Select a venue above to view its lot mappings.' }}
            </p>
            <b-button
              v-if="selectedVenue"
              size="sm"
              variant="outline-primary"
              @click="openAddModal"
            >
              <b-icon icon="plus-circle" class="mr-1"></b-icon> Add the first mapping
            </b-button>
          </div>
        </template>
      </vue-good-table>
    </div>

    <!-- Datalists power the marketplace autocompletes inside the edit modal -->
    <datalist id="stubHubList">
      <option v-for="(opt, index) in stubHubOptions" :key="index" :value="opt.value"></option>
    </datalist>
    <datalist id="vividSeatsList">
      <option v-for="(opt, index) in vividSeatsOptions" :key="index" :value="opt.value"></option>
    </datalist>
    <datalist id="seatGeekList">
      <option v-for="(opt, index) in seatGeekOptions" :key="index" :value="opt.value"></option>
    </datalist>
  </div>
</template>

<script>
import axios from "axios";
import { BIcon } from "bootstrap-vue";
import { VueGoodTable } from "vue-good-table";
import "vue-good-table/dist/vue-good-table.css";

export default {
  components: {
    BIcon,
    VueGoodTable,
  },
  data() {
    return {
      showModal: false,
      newRecord: {
        venue_name: "",
        section: "",
        stubhub_location_name: "",
        seatgeek_location_name: "",
        vividseats_location_name: "",
        gotickets_location_name: "",
        justpark_location_name: "",
        parkwhiz_location_name: "",
        parkmobile_location_name: "",
        spothero_location_name: "",
        tcntix_venue_id: "0",
        vivid_venue_id: "0",
      },
      isEditing: false,
      selectedVenue: null,
      venueOptions: [],
      tableBusy: false,
      lots: [],
      pwOptions: [{ value: null, text: "None" }],
      pmOptions: [{ value: null, text: "None" }],
      spthroOptions: [{ value: null, text: "None" }],
      justParkOptions: [{ value: null, text: "None" }],
      seatGeekOptions: [{ value: null, text: "None" }],
      stubHubOptions: [{ value: null, text: "None" }],
      vividSeatsOptions: [{ value: null, text: "None" }],
      columns: [
        {
          field: "section",
          label: "SpotJockey Section",
          thClass: "col-section",
          tdClass: "col-section",
        },
        {
          field: "stubhub_location_name",
          label: "StubHub",
          thClass: "col-resale",
          tdClass: "col-resale",
        },
        {
          field: "seatgeek_location_name",
          label: "SeatGeek",
          thClass: "col-resale",
          tdClass: "col-resale",
        },
        {
          field: "vividseats_location_name",
          label: "VividSeats",
          thClass: "col-resale",
          tdClass: "col-resale",
        },
        {
          field: "gotickets_location_name",
          label: "GoTickets",
          thClass: "col-resale",
          tdClass: "col-resale",
        },
        {
          field: "justpark_location_name",
          label: "JustPark",
          thClass: "col-parking",
          tdClass: "col-parking",
        },
        {
          field: "parkwhiz_location_name",
          label: "ParkWhiz",
          thClass: "col-parking",
          tdClass: "col-parking",
        },
        {
          field: "parkmobile_location_name",
          label: "ParkMobile",
          thClass: "col-parking",
          tdClass: "col-parking",
        },
        {
          field: "spothero_location_name",
          label: "SpotHero",
          thClass: "col-parking",
          tdClass: "col-parking",
        },
        {
          field: "actions",
          label: "Actions",
          sortable: false,
          thClass: "col-actions text-center",
          tdClass: "col-actions text-center",
        },
      ],
    };
  },
  methods: {
    async fetchLotsByVenue(venueName) {
      this.tableBusy = true;
      try {
        const response = await axios.get(
          `https://api.spotjockeys.com/api/lotmap`,
          {
            params: { venue_name: venueName },
          }
        );
        this.lots = response.data;
        await Promise.all([
          this.fetchPwLotName(),
          this.fetchPmLotName(),
          this.fetchSptHroLotName(),
          this.fetchJustParkLotName(),
          this.fetchSeatGeekLotName(),
          this.fetchStubHubLotName(),
          this.fetchVividSeatsLotName(),
        ]);
      } catch (error) {
        console.error("Error fetching lots data:", error);
      } finally {
        this.tableBusy = false;
      }
    },
    openAddModal() {
      if (!this.selectedVenue) return;
      this.isEditing = false;
      this.resetNewRecord();
      this.showModal = true;
    },
    resetNewRecord() {
      this.newRecord = {
        section: "",
        stubhub_location_name: "",
        seatgeek_location_name: "",
        vividseats_location_name: "",
        gotickets_location_name: "",
        justpark_location_name: "",
        parkwhiz_location_name: "",
        parkmobile_location_name: "",
        spothero_location_name: "",
        tcntix_venue_id: "0",
        vivid_venue_id: "0",
      };
    },
    onRowClick(params) {
      // Ignore clicks on the row action icons; those handle themselves
      if (params.event.target.closest('.action-icons')) return;
      this.editRow(params.row);
    },
    editRow(row) {
      this.isEditing = true;
      // Deep copy to avoid binding issues with the table
      this.newRecord = JSON.parse(JSON.stringify(row));
      this.showModal = true;
    },
    handleModalOk(bvModalEvt) {
      // Prevent modal close
      bvModalEvt.preventDefault();
      
      if (this.isEditing) {
        this.submitUpdateRecord();
      } else {
        this.addRecord();
      }
    },
    async submitUpdateRecord() {
      try {
        await this.updateRow(this.newRecord);

        // Optimistic update of local data
        const index = this.lots.findIndex(l => l.id === this.newRecord.id);
        if (index !== -1) {
          this.$set(this.lots, index, { ...this.newRecord });
        }
        
        this.showModal = false;
        this.resetNewRecord();
      } catch (error) {
        console.error("Error updating record:", error);
      }
    },
    addRecord() {
      this.submitNewRecord();
      this.lots.push({ ...this.newRecord });
      
      this.resetNewRecord();
       // Close modal manually since we prevented default
      this.showModal = false;
    },
    async submitNewRecord() {
      try {
        console.log(this.newRecord);
        console.log(this.selectedVenue + " Parking");
        this.newRecord.venue_name = this.selectedVenue + " Parking";
        // Add API call here
        const response = await axios.post(
          "https://api.spotjockeys.com/api/lot-mapping",
          this.newRecord
        );
        console.log(response);
        this.showModal = false;
        this.fetchLotsByVenue(this.selectedVenue);
      } catch (error) {
        console.error("Error adding new record:", error);
      }
    },
    onVenueChange() {
      this.fetchLotsByVenue(this.selectedVenue);
      console.log(this.selectedVenue);
    },
    async fetchVenues() {
      try {
        const response = await axios.get("https://api.spotjockeys.com/api/venues");
        this.venueOptions = response.data.map((venue) => ({
          value: venue.name,
          text: venue.name,
        }));
      } catch (error) {
        console.error("Error fetching venues:", error);
      }
    },
    async fetchPwLotName() {
      this.tableBusy = true;
      try {
        const response = await axios.get("https://api.spotjockeys.com/api/pw/lots", {
          params: { venue_name: this.selectedVenue },
        });
        this.pwOptions = [
          { value: null, text: "None" },
          ...response.data.map((loc) => ({
            value: loc.location_name,
            text: loc.location_name,
          })),
        ];
      } catch (error) {
        console.error("Error fetching ParkWhiz options:", error);
      } finally {
        this.tableBusy = false;
      }
    },
    async fetchPmLotName() {
      this.tableBusy = true;
      try {
        const response = await axios.get("https://api.spotjockeys.com/api/pm/lots", {
          params: { venue_name: this.selectedVenue },
        });
        this.pmOptions = [
          { value: null, text: "None" },
          ...response.data.map((loc) => ({
            value: loc.location_name,
            text: loc.location_name,
          })),
        ];
      } catch (error) {
        console.error("Error fetching ParkMobile options:", error);
      } finally {
        this.tableBusy = false;
      }
    },
    async fetchSptHroLotName() {
      this.tableBusy = true;
      try {
        const response = await axios.get("https://api.spotjockeys.com/api/spthro/lots", {
          params: { venue_name: this.selectedVenue },
        });
        this.spthroOptions = [
          { value: null, text: "None" },
          ...response.data.map((loc) => ({
            value: loc.location_name,
            text: loc.location_name,
          })),
        ];
        this.tableBusy = false;
      } catch (error) {
        console.error("Error fetching SpotHero options:", error);
      } finally {
        this.tableBusy = false;
      }
    },
    async fetchJustParkLotName() {
      this.tableBusy = true;
      try {
        const response = await axios.get("https://api.spotjockeys.com/api/justpark/lots", {
          params: { venue_name: this.selectedVenue },
        });
        this.justParkOptions = [
          { value: null, text: "None" },
          ...response.data.map((loc) => ({
            value: loc.location_name,
            text: loc.location_name,
          })),
        ];
      } catch (error) {
        console.error("Error fetching JustPark options:", error);
      } finally {
        this.tableBusy = false;
      }
    },
    async fetchStubHubLotName() {
      this.tableBusy = true;
      try {
        const response = await axios.get("https://api.spotjockeys.com/api/stubhub/lots", {
          params: { venue_name: this.selectedVenue },
        });
        this.stubHubOptions = [
          { value: null, text: "None" },
          ...response.data.map((loc) => ({
            value: loc.location_name,
            text: loc.location_name,
          })),
        ];
      } catch (error) {
        console.error("Error fetching StubHub options:", error);
      } finally {
        this.tableBusy = false;
      }
    },
    async fetchSeatGeekLotName() {
      this.tableBusy = true;
      try {
        const response = await axios.get("https://api.spotjockeys.com/api/seatgeek/lots", {
          params: { venue_name: this.selectedVenue },
        });
        this.seatGeekOptions = [
          { value: null, text: "None" },
          ...response.data.map((loc) => ({
            value: loc.location_name,
            text: loc.location_name,
          })),
        ];
      } catch (error) {
        console.error("Error fetching SeatGeek options:", error);
      } finally {
        this.tableBusy = false;
      }
    },
    async fetchVividSeatsLotName() {
      this.tableBusy = true;
      try {
        const response = await axios.get("https://api.spotjockeys.com/api/vividseats/lots", {
          params: { venue_name: this.selectedVenue },
        });
        this.vividSeatsOptions = [
          { value: null, text: "None" },
          ...response.data.map((loc) => ({
            value: loc.location_name,
            text: loc.location_name,
          })),
        ];
      } catch (error) {
        console.error("Error fetching VividSeats options:", error);
      } finally {
        this.tableBusy = false;
      }
    },
    makeToast(message, options) {
      this.$toast(message, {
        timeout: 2000,
        type: options.type || "info",
        position: "top-center",
        closeOnClick: true,
        pauseOnFocusLoss: true,
        pauseOnHover: true,
        draggable: true,
        draggablePercent: 0.6,
        showCloseButtonOnHover: false,
        hideProgressBar: true,
        closeButton: "button",
        icon: true,
        rtl: false,
      });
    },
    async updateRow(row) {
      try {
        const response = await axios.post(
          "https://api.spotjockeys.com/api/updateLotMap",
          {
            id: row.id,
            pw_name: row.parkwhiz_location_name,
            pm_name: row.parkmobile_location_name,
            spthro_name: row.spothero_location_name,
            stubhub_location_name: row.stubhub_location_name,
            seatgeek_location_name: row.seatgeek_location_name,
            vividseats_location_name: row.vividseats_location_name,
            gotickets_location_name: row.gotickets_location_name,
            justpark_location_name: row.justpark_location_name,
          }
        );
        this.makeToast("Record Updated Successfully", { type: "success" });
        console.log("Row updated successfully", response.data);
      } catch (error) {
        console.error("Error updating row:", error);
      }
    },
    async deleteRow(id) {
      if (confirm("Are you sure you want to delete this record?")) {
        try {
          await axios.delete(`https://api.spotjockeys.com/api/lot-mapping/${id}`);
          this.lots = this.lots.filter((lot) => lot.id !== id);
          this.makeToast("Record deleted successfully", { type: "success" });
        } catch (error) {
          console.error("Error deleting row:", error);
          this.makeToast("Failed to delete record", { type: "error" });
        }
      }
    },
    async duplicateRow(row) {
      // Create a new object with the same values as the item, but with an empty ID to avoid conflicts when creating the new record in the database
      const newItem = {
        venue_name: row.venue_name,
        section: row.section,
        stubhub_location_name: row.stubhub_location_name,
        seatgeek_location_name: row.seatgeek_location_name,
        vividseats_location_name: row.vividseats_location_name,
        gotickets_location_name: row.gotickets_location_name,
        justpark_location_name: row.justpark_location_name,
        parkwhiz_location_name: row.parkwhiz_location_name,
        parkmobile_location_name: row.parkmobile_location_name,
        spothero_location_name: row.spothero_location_name,
        tcntix_venue_id: row.tcntix_venue_id,
        vivid_venue_id: row.vivid_venue_id,
      };

      try {
        // Call the API to create a new lot mapping record with the values from the copied item
        const response = await axios.post(
          "https://api.spotjockeys.com/api/lot-mapping",
          newItem
        );

        // Update the lots array with the new record returned from the API
         this.lots = [...this.lots, response.data];

        this.makeToast("Record Duplicated Successfully", { type: "success" });
        this.fetchLotsByVenue(this.selectedVenue);
      } catch (error) {
        console.error("Error duplicating record:", error);
        this.makeToast("Failed to duplicate record", { type: "error" });
      }
    },
  },
  async mounted() {
    await this.fetchVenues();
  },
};
</script>

<style scoped>
.lotmap {
  margin-top: 0.5rem;
}

/* ---- Toolbar ---- */
.lotmap-toolbar {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 1rem;
  padding: 1rem 1.25rem;
  margin-bottom: 1rem;
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 0.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}

.toolbar-venue {
  display: flex;
  flex-direction: column;
  min-width: 260px;
  flex: 1 1 260px;
  max-width: 360px;
}

.toolbar-label {
  margin-bottom: 0.35rem;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #6c757d;
}

.venue-select {
  border: 1px solid #ced4da;
  border-radius: 0.375rem;
  padding: 0.5rem 0.75rem;
  font-size: 1rem;
  line-height: 1.5;
  color: #495057;
  background-color: #fff;
  width: 100%;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}

.venue-select:focus {
  border-color: #80bdff;
  outline: 0;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

.toolbar-actions {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.count-badge {
  font-size: 0.8rem;
  font-weight: 600;
  color: #495057;
  border: 1px solid #e9ecef;
  padding: 0.4rem 0.6rem;
}

/* ---- Table ---- */
.table-responsive {
  overflow-x: auto;
  border-radius: 0.5rem;
}

.vgt-table {
  width: 100%;
}

.vgt-table th,
.vgt-table td {
  white-space: nowrap;
  vertical-align: middle;
}

/* Clickable rows */
::v-deep .clickable-rows tbody tr {
  cursor: pointer;
  transition: background-color 0.12s ease-in-out;
}

::v-deep .clickable-rows tbody tr:hover {
  background-color: #eef5ff !important;
}

/* Column-group tinting so resale vs. parking read at a glance */
::v-deep th.col-section {
  background: #eef1f5;
}
::v-deep td.col-section {
  font-weight: 600;
  color: #212529;
  background: #f8f9fa;
}
::v-deep th.col-resale {
  background: #eaf3ec;
  color: #1c6b3b;
}
::v-deep th.col-parking {
  background: #eef0f8;
  color: #364a9e;
}

/* Sticky section column keeps the row label visible when scrolling wide */
::v-deep th.col-section,
::v-deep td.col-section {
  position: sticky;
  left: 0;
  z-index: 2;
}

.section-cell {
  font-weight: 600;
}

.mapping-value {
  color: #343a40;
}

.mapping-empty {
  color: #ced4da;
}

/* ---- Row actions ---- */
.action-icons {
  display: flex;
  gap: 0.75rem;
  justify-content: center;
}

.action-icon {
  font-size: 1.15rem;
  cursor: pointer;
  opacity: 0.8;
  transition: opacity 0.12s ease-in-out, transform 0.12s ease-in-out;
}

.action-icon:hover {
  opacity: 1;
  transform: scale(1.15);
}

/* ---- Empty state ---- */
.empty-state {
  text-align: center;
  padding: 2.5rem 1rem;
  color: #6c757d;
}

.empty-icon {
  font-size: 2.25rem;
  color: #ced4da;
  margin-bottom: 0.75rem;
}

/* ---- Modal inputs (still used by the edit/add form) ---- */
.table-cell-input {
  border: 1px solid #ced4da;
  border-radius: 0.25rem;
  padding: 0.375rem 0.75rem;
  font-size: 1rem;
  line-height: 1.5;
  color: #495057;
  background-color: #fff;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}

.table-cell-select {
  border: 1px solid #ced4da;
  border-radius: 0.25rem;
  padding: 0.375rem 0.75rem;
  font-size: 1rem;
  line-height: 1.5;
  color: #495057;
  background-color: #fff;
  width: 100%;
  display: block;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}

.table-cell-input:focus,
.table-cell-select:focus {
  border-color: #80bdff;
  outline: 0;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

/* Sort-direction arrows */
::v-deep .vgt-table .ascending,
::v-deep .vgt-table .descending {
  position: relative;
  padding-right: 1.5em;
}

::v-deep .vgt-table .ascending::after,
::v-deep .vgt-table .descending::after {
  content: "";
  position: absolute;
  top: 50%;
  right: 0.5em;
  width: 0;
  height: 0;
  border-left: 0.5em solid transparent;
  border-right: 0.5em solid transparent;
}

::v-deep .vgt-table .ascending::after {
  border-bottom: 0.5em solid #495057;
  transform: translateY(-150%);
}

::v-deep .vgt-table .descending::after {
  border-top: 0.5em solid #495057;
  transform: translateY(-50%);
}
</style>
